/**
 * Espelho durável do save da carreira.
 *
 * O IndexedDB continua sendo o armazenamento quente, dentro do Web Worker,
 * exatamente como antes. Este arquivo roda na thread da interface e faz uma
 * cópia do save num SQLite que mora fora da pasta de perfil da WebView.
 *
 * Os megabytes do mundo atravessam a fronteira do worker uma vez por rodada, o
 * que é um preço justo por não perder a carreira. O comentário no topo de
 * `save-store.ts` recusa esse custo para *toda leitura*, e isso continua de pé:
 * ler o save para desenhar a tela nunca passa por aqui.
 *
 * Duas regras mandam em tudo que está abaixo:
 *
 * 1. Falhar no espelho nunca derruba o jogo. Se o SQLite não abrir, não gravar
 *    ou corromper, o jogo segue no IndexedDB e o problema vira aviso. Quem
 *    perde durabilidade ainda joga; quem perde o jogo não joga nada.
 * 2. O espelho copia, não manda. O IndexedDB é a verdade enquanto existir. Só
 *    quando ele perde um save é que o espelho tem alguma coisa a dizer.
 */

import { listSaves, readSave, writeSave } from "../save-store";
import { abrirArmazenamentoDuravel } from "./escolha";
import type { ArmazenamentoDuravel, Registrador, ResultadoDaSincronia, SaveMeta } from "./porta";

/**
 * Mesmo teto que o worker aplica ao IndexedDB.
 *
 * Não dá para o espelho seguir as exclusões do worker: quando o IndexedDB some
 * inteiro, apagar no espelho o que sumiu lá apagaria justamente o que ele
 * existe para guardar. Então ele aplica a própria poda, por contagem.
 */
const MAX_ESPELHADOS = 8;

/**
 * Até quando a abertura do jogo espera pela restauração.
 *
 * Uma ponte nativa quebrada não devolve erro: ela simplesmente não responde. E
 * como o primeiro comando da tela espera pela restauração, ficar esperando para
 * sempre travaria o jogo numa tela de carregamento sem dizer por quê, que é
 * pior que falhar. Passado o prazo o jogo abre com o que o IndexedDB tiver, e a
 * restauração segue no fundo se ainda for chegar.
 *
 * Dez segundos é folgado de propósito: a espera longa só acontece quando há
 * mesmo o que restaurar, e nesse caso é a carreira inteira que está em jogo.
 */
const LIMITE_DE_ESPERA_MS = 10_000;

function comoTexto(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}

function datasPorId(metas: SaveMeta[]): Map<string, number> {
  return new Map(metas.map((meta) => [meta.id, meta.updatedAt]));
}

/**
 * O save vale a cópia se o outro lado não o tem ou tem versão mais velha.
 *
 * É esta comparação que impede o espelho de recopiar megabytes a cada rodada
 * para os saves que ninguém tocou.
 */
function valeCopiar(meta: SaveMeta, doOutroLado: Map<string, number>): boolean {
  const existente = doOutroLado.get(meta.id);
  return existente === undefined || existente < meta.updatedAt;
}

async function podar(destino: ArmazenamentoDuravel, manter: number): Promise<void> {
  const guardados = [...(await destino.listar())].sort((a, b) => b.updatedAt - a.updatedAt);
  for (const meta of guardados.slice(manter)) await destino.apagar(meta.id);
}

/** Do IndexedDB para o durável. É o passo que roda depois de cada gravação. */
export async function copiarParaDuravel(
  destino: ArmazenamentoDuravel,
  manter: number = MAX_ESPELHADOS,
): Promise<ResultadoDaSincronia> {
  const guardados = datasPorId(await destino.listar());
  let copiados = 0;
  let ignorados = 0;

  for (const meta of await listSaves()) {
    if (!valeCopiar(meta, guardados)) {
      ignorados += 1;
      continue;
    }
    const registro = await readSave(meta.id);
    // O worker pode ter podado este save entre a listagem e a leitura. Sumiu do
    // quente antes de chegar aqui, então não tem por que entrar no espelho.
    if (!registro) {
      ignorados += 1;
      continue;
    }
    await destino.gravar(registro);
    copiados += 1;
  }

  await podar(destino, manter);
  return { copiados, ignorados };
}

/**
 * Do durável para o IndexedDB. É o passo que salva a carreira de quem abriu o
 * jogo depois de o sistema limpar a pasta da WebView.
 */
export async function restaurarDoDuravel(origem: ArmazenamentoDuravel): Promise<ResultadoDaSincronia> {
  const quentes = datasPorId(await listSaves());
  let copiados = 0;
  let ignorados = 0;

  for (const meta of await origem.listar()) {
    if (!valeCopiar(meta, quentes)) {
      ignorados += 1;
      continue;
    }
    const registro = await origem.ler(meta.id);
    if (!registro) {
      ignorados += 1;
      continue;
    }
    await writeSave(registro);
    copiados += 1;
  }

  return { copiados, ignorados };
}

/**
 * O que dá para fazer no navegador puro.
 *
 * Não existe pasta fora do alcance do navegador para onde copiar, então o
 * pedido de armazenamento persistente é o equivalente possível: ele tira o
 * IndexedDB da fila do que o navegador descarta quando o disco aperta. É pouco
 * código e cobre boa parte do risco na web.
 *
 * O navegador pode recusar sem dizer por quê, e recusar não é erro: o jogo
 * funciona igual, só sem a garantia.
 */
export async function pedirArmazenamentoPersistente(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined") return false;
    const armazenamento: StorageManager | undefined = navigator.storage;
    if (typeof armazenamento?.persist !== "function") return false;
    if (await armazenamento.persisted()) return true;
    return await armazenamento.persist();
  } catch {
    return false;
  }
}

export interface OpcoesDoEspelho {
  /** Trocado no teste por um armazenamento de mentira, em memória. */
  abrir?: () => Promise<ArmazenamentoDuravel | null>;
  /**
   * Para onde vão os avisos.
   *
   * Injetado em vez de importado: o espelho não tem por que conhecer o Sentry,
   * e assim o teste roda sem arrastar o relatório de erros junto. Quem liga os
   * dois é `runner.ts`.
   */
  registrar?: Registrador;
  manter?: number;
  /** Prazo da restauração na abertura. O teste encurta para não esperar. */
  espera?: number;
}

export interface Espelho {
  /** Restaura o que faltar. Chamado uma vez, antes do primeiro comando. */
  preparar(): Promise<void>;
  /** Copia o que mudou. Chamado depois de cada gravação do worker. */
  aoGravar(): Promise<void>;
  /** Exclusão pedida pelo jogador precisa valer nos dois lados. */
  aoApagar(id: string): Promise<void>;
  encerrar(): Promise<void>;
}

export function criarEspelho(opcoes: OpcoesDoEspelho = {}): Espelho {
  const abrir = opcoes.abrir ?? abrirArmazenamentoDuravel;
  const registrar: Registrador = opcoes.registrar ?? (() => {});
  const manter = opcoes.manter ?? MAX_ESPELHADOS;
  const espera = opcoes.espera ?? LIMITE_DE_ESPERA_MS;

  let duravel: ArmazenamentoDuravel | null = null;
  let abertura: Promise<ArmazenamentoDuravel | null> | null = null;
  let preparacao: Promise<void> | null = null;
  let fila: Promise<void> = Promise.resolve();
  let agendado: Promise<void> | null = null;
  let falhando = false;

  /** A tarefa continua na fila; só a espera de quem chamou tem prazo. */
  function comPrazo(tarefa: Promise<void>): Promise<void> {
    return new Promise<void>((resolve) => {
      const relogio: ReturnType<typeof setTimeout> = setTimeout(() => {
        registrar("O espelho durável demorou demais. O jogo abriu com o que estava no IndexedDB.");
        resolve();
      }, espera);
      void tarefa.then(() => {
        clearTimeout(relogio);
        resolve();
      });
    });
  }

  function garantirDuravel(): Promise<ArmazenamentoDuravel | null> {
    // Uma tentativa só por sessão. Se o SQLite não abriu, não vai abrir na
    // rodada seguinte, e insistir a cada gravação custaria caro por nada.
    if (!abertura) {
      abertura = abrir()
        .then((aberto) => {
          duravel = aberto;
          return aberto;
        })
        .catch((erro: unknown) => {
          registrar("O espelho durável do save não abriu. O jogo segue com o IndexedDB.", {
            erro: comoTexto(erro),
          });
          return null;
        });
    }
    return abertura;
  }

  /**
   * Onde mora a promessa de que o espelho não derruba o jogo.
   *
   * Toda tarefa passa por aqui e nenhuma rejeição escapa. A fila em série
   * também importa: uma exclusão não pode ser ultrapassada por uma cópia que
   * devolveria o save apagado ao espelho.
   */
  function enfileirar(aviso: string, tarefa: () => Promise<void>): Promise<void> {
    const proximo = fila.then(tarefa).then(
      () => {
        falhando = false;
      },
      (erro: unknown) => {
        // Só a primeira falha da sequência vira aviso. Um disco cheio repetiria
        // a mesma mensagem a cada rodada e afogaria o relatório de erros.
        if (!falhando) registrar(aviso, { armazenamento: duravel?.nome, erro: comoTexto(erro) });
        falhando = true;
      },
    );
    fila = proximo;
    return proximo;
  }

  return {
    preparar(): Promise<void> {
      if (preparacao) return preparacao;
      const tarefa = enfileirar("Não foi possível restaurar os saves do espelho durável.", async () => {
        const origem = await garantirDuravel();
        // Sem espelho por ser navegador puro, ou por o SQLite não ter aberto.
        // Nos dois casos o que sobra é pedir ao navegador que não descarte o
        // IndexedDB, e nos dois casos vale a pena pedir.
        if (!origem) {
          await pedirArmazenamentoPersistente();
          return;
        }
        const resultado = await restaurarDoDuravel(origem);
        if (resultado.copiados > 0) {
          registrar("Saves recuperados do espelho durável.", {
            armazenamento: origem.nome,
            copiados: resultado.copiados,
          });
        }
      });
      preparacao = comPrazo(tarefa);
      return preparacao;
    },

    aoGravar(): Promise<void> {
      // Uma passagem já esperando cobre esta chamada também: ela só vai ler o
      // IndexedDB quando começar, e aí já enxerga a gravação mais nova. Sem
      // isto, duas rodadas seguidas copiariam os mesmos megabytes duas vezes.
      if (agendado) return agendado;
      const proximo = enfileirar("Não foi possível espelhar o save.", async () => {
        agendado = null;
        const destino = await garantirDuravel();
        if (!destino) return;
        await copiarParaDuravel(destino, manter);
      });
      agendado = proximo;
      return proximo;
    },

    aoApagar(id: string): Promise<void> {
      return enfileirar("Não foi possível apagar o save do espelho durável.", async () => {
        const destino = await garantirDuravel();
        if (!destino) return;
        await destino.apagar(id);
      });
    },

    encerrar(): Promise<void> {
      return enfileirar("Não foi possível fechar o espelho durável.", async () => {
        // Sem `garantirDuravel` de propósito: uma sessão que nunca precisou do
        // espelho abriria o SQLite aqui só para fechar na linha seguinte.
        if (!abertura) return;
        const aberto = await abertura;
        if (!aberto) return;
        await aberto.fechar();
      });
    },
  };
}
